import * as React from 'react';
import { ChevronDown, Copy, ExternalLink } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../components/ui/toast';
import { TWILIO_INBOUND_WEBHOOK } from '../../../../lib/voiceAgent';
import { cn } from '../../../../lib/utils';

export function TwilioSetupHelp() {
  const [open, setOpen] = React.useState(false);
  const { toast } = useToast();

  async function copy(text: string, label = 'In Zwischenablage kopiert') {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: label });
    } catch {
      toast({ title: 'Kopieren fehlgeschlagen', variant: 'destructive' });
    }
  }

  return (
    <div className="rounded-md border border-border bg-muted/20">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium hover:bg-muted/30"
      >
        <span>Wie richte ich den Twilio-Webhook ein?</span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="space-y-4 border-t border-border px-4 py-4 text-sm">
          <Step n={1} title="Twilio-Konsole öffnen">
            <p className="text-muted-foreground">
              Gehe zur{' '}
              <a
                href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Twilio Phone Numbers Übersicht
                <ExternalLink className="h-3 w-3" />
              </a>{' '}
              und klicke auf die Nummer, die der Voice-Agent annehmen soll.
            </p>
          </Step>
          <Step n={2} title="Voice-Konfiguration finden">
            <p className="text-muted-foreground">
              Scrolle zum Bereich <em>„Voice Configuration"</em>. Bei „Configure with" wähle
              <em>„Webhook, TwiML Bin, Function, Studio Flow, Proxy Service"</em>.
            </p>
          </Step>
          <Step n={3} title="Webhook-URL eintragen">
            <p className="text-muted-foreground">
              Bei <em>„A call comes in"</em> wähle <strong>Webhook</strong>, Methode{' '}
              <strong>HTTP POST</strong>, und füge folgende URL ein:
            </p>
            <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 font-mono text-xs">
              <code className="flex-1 break-all">{TWILIO_INBOUND_WEBHOOK}</code>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(TWILIO_INBOUND_WEBHOOK)}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Step>
          <Step n={4} title="Speichern + Anruf testen">
            <p className="text-muted-foreground">
              Klicke unten auf <strong>Save configuration</strong>. Anschließend kannst du die
              Nummer anrufen — der Voice-Agent grüßt und nimmt das Gespräch entgegen.
            </p>
          </Step>
          <Step n={5} title="(Optional) Signatur-Verifikation">
            <p className="text-muted-foreground">
              Trage deinen Twilio Auth-Token unten in das Feld „Auth Token" ein und speichere die
              Einstellungen. Der Voice-Agent verifiziert dann die Twilio-Signatur jedes Webhook-Calls.
              Außerdem kannst du den Token im Tab „API-Schlüssel" projektweit als
              <code className="mx-1 rounded bg-muted px-1">TWILIO_AUTH_TOKEN</code> hinterlegen.
            </p>
          </Step>
        </div>
      )}
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
        {n}
      </div>
      <div className="flex-1">
        <div className="font-medium">{title}</div>
        <div className="mt-1 text-sm">{children}</div>
      </div>
    </div>
  );
}
